import React, { useState } from 'react'
import {
  Box,
  VStack,
  FormControl,
  FormLabel,
  FormHelperText,
  FormErrorMessage,
  Input,
  Textarea,
  Button,
  Alert,
  AlertIcon,
  Stat,
  StatLabel,
  StatNumber,
  SimpleGrid,
  Text,
  useColorModeValue,
  useDisclosure,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
} from '@chakra-ui/react'
import { FiPlay } from 'react-icons/fi'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@tanstack/react-query'
import { contractService } from '@/services/admin/contractService'
import type { ContractPreparationTriggerResult } from '@/services/admin/contractService'
import { LifecycleBatchItemResults } from './LifecycleBatchItemResults'
import {
  getTriggerProgressStatus,
  ProgressModal,
  type ProgressStatus,
} from '@/components/common/ProgressModal'
import {
  findEntriesWithoutPrefix,
  parseMultilineEntries,
} from '@/components/common/triggerInputHelpers'
import { useToast } from '@/hooks/useToast'
import { useNetworkEnvironment } from '@/hooks/useNetworkEnvironment'

export const ContractPreparationTrigger: React.FC = () => {
  const { t } = useTranslation()
  const toast = useToast()
  const { currentEnv } = useNetworkEnvironment()
  const bgColor = useColorModeValue('white', 'gray.800')
  const borderColor = useColorModeValue('gray.200', 'gray.700')
  const { isOpen: isProgressOpen, onOpen: onProgressOpen, onClose: onProgressClose } = useDisclosure()

  const [merchantId, setMerchantId] = useState('')
  const [orderIds, setOrderIds] = useState('')
  const [limit, setLimit] = useState(100)
  const [merchantIdError, setMerchantIdError] = useState('')
  const [orderIdsError, setOrderIdsError] = useState('')
  const [results, setResults] = useState<ContractPreparationTriggerResult | null>(null)
  const [progressStatus, setProgressStatus] = useState<ProgressStatus>('idle')
  const [progressCurrent, setProgressCurrent] = useState(0)
  const [progressTotal, setProgressTotal] = useState(0)
  const [progressMessage, setProgressMessage] = useState('')

  const normalizeStat = (value: unknown): number => {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value
    }
    if (typeof value === 'string' && value.trim().length > 0) {
      const parsed = Number(value)
      return Number.isFinite(parsed) ? parsed : 0
    }
    return 0
  }

  const isValidMerchantIdentifier = (value: string): boolean =>
    value.trim().length === 0 || /^[A-Za-z0-9_-]+$/.test(value.trim())

  const executePreparation = async () => {
    if (!isValidMerchantIdentifier(merchantId)) {
      throw new Error(t('monitoring.contractPreparation.errors.invalidMerchantId'))
    }

    // Parse and validate order IDs
    const orderIdList = parseMultilineEntries(orderIds)

    const invalidOrderIds = findEntriesWithoutPrefix(orderIdList, 'ord_')
    if (invalidOrderIds.length > 0) {
      throw new Error(
        t('monitoring.contractPreparation.errors.invalidOrderIds', {
          ids: invalidOrderIds.join(', ')
        })
      )
    }

    const params: any = {
      limit
    }

    const normalizedMerchantId = merchantId.trim()
    if (normalizedMerchantId) {
      params.merchantId = normalizedMerchantId
    }

    if (orderIdList.length > 0) {
      params.orderIds = orderIdList
    }

    setProgressTotal(0)
    setProgressCurrent(0)
    setProgressMessage(t('monitoring.contractPreparation.progress.scanning'))
    setProgressStatus('processing')
    onProgressOpen()

    return await contractService.triggerContractPreparation(params, currentEnv)
  }

  const mutation = useMutation({
    mutationFn: executePreparation,
    onSuccess: (response) => {
      if (response) {
        const ordersScanned = normalizeStat(response.ordersScanned)
        const ordersQueued = normalizeStat(response.ordersQueued)
        const alreadyQueued = normalizeStat(response.alreadyQueued)
        const failed = normalizeStat(response.failed)
        const ordersSkipped =
          response.ordersSkipped !== undefined
            ? normalizeStat(response.ordersSkipped)
            : Math.max(0, ordersScanned - (ordersQueued + alreadyQueued + failed))

        setResults({
          ordersScanned,
          ordersQueued,
          ordersSkipped,
          alreadyQueued,
          failed,
          items: response.items,
          message: response.message,
        })
        
        setProgressCurrent(ordersScanned)
        setProgressTotal(ordersScanned)
        setProgressStatus(getTriggerProgressStatus(failed))
        setProgressMessage(
          response.message || t('monitoring.contractPreparation.progress.completed')
        )
        
        if (failed > 0 || ordersQueued > 0) {
          const toastMethod = failed > 0 ? toast.warning : toast.success
          toastMethod({
            title: t('monitoring.contractPreparation.success.title'),
            description: t('monitoring.contractPreparation.success.description', {
              count: ordersQueued,
              failed,
            }),
            duration: 5000,
            isClosable: true,
          })
        } else {
          toast.info({
            title: t('monitoring.contractPreparation.success.noOrdersTitle'),
            description: t('monitoring.contractPreparation.success.noOrdersDescription', {
              scanned: ordersScanned,
              skipped: ordersSkipped
            }),
            duration: 5000,
            isClosable: true,
          })
        }
      }
    },
    onError: (error: any) => {
      setProgressStatus('failed')
      
      let errorMessage = 'Failed to trigger contract preparation'
      let errorTitle = t('monitoring.contractPreparation.error.title')
      
      if (error.response?.status === 429) {
        errorTitle = t('monitoring.contractPreparation.error.rateLimitTitle')
        errorMessage = t('monitoring.contractPreparation.error.rateLimitMessage')
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error
      } else if (error.message) {
        errorMessage = error.message
      }
      
      setProgressMessage(errorMessage)
      
      toast.error({
        title: errorTitle,
        description: errorMessage,
        duration: 8000,
        isClosable: true,
      })
    },
  })

  const handleMerchantIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setMerchantId(value)
    
    if (!isValidMerchantIdentifier(value)) {
      setMerchantIdError(t('monitoring.contractPreparation.errors.invalidMerchantId'))
    } else {
      setMerchantIdError('')
    }
  }

  const handleOrderIdsChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value
    setOrderIds(value)
    
    if (value) {
      const orderIdList = parseMultilineEntries(value)
      const invalidOrderIds = findEntriesWithoutPrefix(orderIdList, 'ord_')
      if (invalidOrderIds.length > 0) {
        setOrderIdsError(
          t('monitoring.contractPreparation.errors.invalidOrderIds', {
            ids: invalidOrderIds.slice(0, 3).join(', ')
          })
        )
      } else {
        setOrderIdsError('')
      }
    } else {
      setOrderIdsError('')
    }
  }

  const handleSubmit = () => {
    if (merchantIdError || orderIdsError) {
      return
    }
    
    mutation.mutate()
  }

  const normalizedResults = results
    ? {
        ordersScanned: normalizeStat(results.ordersScanned),
        ordersQueued: normalizeStat(results.ordersQueued),
        ordersSkipped: normalizeStat(results.ordersSkipped),
        alreadyQueued: normalizeStat(results.alreadyQueued),
        failed: normalizeStat(results.failed),
        items: results.items,
        message: results.message,
      }
    : null;

  const ordersSkippedDisplay = normalizedResults
    ? normalizedResults.ordersSkipped ??
        Math.max(
          0,
          normalizedResults.ordersScanned -
            (normalizedResults.ordersQueued +
              normalizedResults.alreadyQueued +
              normalizedResults.failed)
        )
    : 0;

  return (
    <VStack spacing={6} align="stretch">
      <Alert status="info" borderRadius="md">
        <AlertIcon />
        <Box>
          <Text>{t('monitoring.contractPreparation.description')}</Text>
        </Box>
      </Alert>

      <Box>
        <VStack spacing={4} align="stretch">
          <FormControl isInvalid={!!merchantIdError}>
            <FormLabel>{t('monitoring.contractPreparation.fields.merchantId.label')}</FormLabel>
            <Input
              placeholder={t('monitoring.contractPreparation.fields.merchantId.placeholder')}
              value={merchantId}
              onChange={handleMerchantIdChange}
              isDisabled={mutation.isPending}
            />
            {!merchantIdError && (
              <FormHelperText>
                {t('monitoring.contractPreparation.fields.merchantId.helper')}
              </FormHelperText>
            )}
            <FormErrorMessage>{merchantIdError}</FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={!!orderIdsError}>
            <FormLabel>{t('monitoring.contractPreparation.fields.orderIds.label')}</FormLabel>
            <Textarea
              placeholder={t('monitoring.contractPreparation.fields.orderIds.placeholder')}
              value={orderIds}
              onChange={handleOrderIdsChange}
              rows={4}
              isDisabled={mutation.isPending}
            />
            {!orderIdsError && (
              <FormHelperText>
                {t('monitoring.contractPreparation.fields.orderIds.helper')}
              </FormHelperText>
            )}
            <FormErrorMessage>{orderIdsError}</FormErrorMessage>
          </FormControl>

          <FormControl>
            <FormLabel>{t('monitoring.contractPreparation.fields.limit.label')}</FormLabel>
            <NumberInput
              value={limit}
              onChange={(_, value) => setLimit(value || 100)}
              min={1}
              max={500}
              isDisabled={mutation.isPending}
            >
              <NumberInputField />
              <NumberInputStepper>
                <NumberIncrementStepper />
                <NumberDecrementStepper />
              </NumberInputStepper>
            </NumberInput>
            <FormHelperText>
              {t('monitoring.contractPreparation.fields.limit.helper')}
            </FormHelperText>
          </FormControl>

          <Button
            colorScheme="blue"
            leftIcon={<FiPlay />}
            onClick={handleSubmit}
            isLoading={mutation.isPending}
            loadingText={t('monitoring.contractPreparation.button.loading')}
            isDisabled={mutation.isPending || !!merchantIdError || !!orderIdsError}
          >
            {t('monitoring.contractPreparation.button.submit')}
          </Button>
        </VStack>
      </Box>

      {normalizedResults && (
        <Box
          bg={bgColor}
          p={4}
          borderRadius="md"
          borderWidth={1}
          borderColor={borderColor}
        >
          <VStack spacing={4} align="stretch">
            <Box fontWeight="semibold" fontSize="lg">
              {t('monitoring.contractPreparation.results.title')}
            </Box>
            <SimpleGrid columns={{ base: 1, md: 2, lg: 5 }} spacing={4}>
              <Stat>
                <StatLabel>
                  {t('monitoring.contractPreparation.results.ordersScanned')}
                </StatLabel>
                <StatNumber color="blue.600">{normalizedResults.ordersScanned}</StatNumber>
              </Stat>
              <Stat>
                <StatLabel>
                  {t('monitoring.contractPreparation.results.ordersQueued')}
                </StatLabel>
                <StatNumber color="green.600">{normalizedResults.ordersQueued}</StatNumber>
              </Stat>
              <Stat>
                <StatLabel>
                  {t('monitoring.contractPreparation.results.alreadyQueued')}
                </StatLabel>
                <StatNumber color="purple.600">{normalizedResults.alreadyQueued}</StatNumber>
              </Stat>
              <Stat>
                <StatLabel>
                  {t('monitoring.contractPreparation.results.failed')}
                </StatLabel>
                <StatNumber color="red.600">{normalizedResults.failed}</StatNumber>
              </Stat>
              <Stat>
                <StatLabel>
                  {t('monitoring.contractPreparation.results.ordersSkipped')}
                </StatLabel>
                <StatNumber color="orange.600">{ordersSkippedDisplay}</StatNumber>
              </Stat>
            </SimpleGrid>
            {normalizedResults.message && (
              <Text fontSize="sm" fontStyle="italic" color="gray.700">
                {normalizedResults.message}
              </Text>
            )}
            <LifecycleBatchItemResults items={normalizedResults.items} />
          </VStack>
        </Box>
      )}

      {/* Progress Modal */}
      <ProgressModal
        isOpen={isProgressOpen}
        onClose={onProgressClose}
        title={t('monitoring.contractPreparation.progress.title')}
        total={progressTotal}
        current={progressCurrent}
        status={progressStatus}
        message={progressMessage}
        results={results
          ? {
              queued: results.ordersQueued,
              alreadyQueued: results.alreadyQueued ?? 0,
              failed: results.failed ?? 0,
              skipped:
                results.ordersSkipped ??
                Math.max(
                  0,
                  results.ordersScanned -
                    (results.ordersQueued +
                      (results.alreadyQueued ?? 0) +
                      (results.failed ?? 0))
                ),
            }
          : undefined}
        canCancel={false}
      />
    </VStack>
  )
}
